import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronDown, LogOut } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';

export interface SidebarMenuItem {
    label: string;
    path?: string;
    icon: React.ReactNode | ((active: boolean) => React.ReactNode);
    badge?: number | string | null;
    action?: () => void;
    show?: boolean;
    textColor?: string;
    expandable?: boolean;
    subItems?: SidebarMenuItem[]; // For future nested items if needed
    /**
     * Custom content rendered inside the expandable section when expanded.
     * Used by Past chats (David Slice 4.9) to render a dynamic list of
     * recent sessions with click-through navigation. Takes precedence over
     * subItems (kept for backward compat / static menus).
     */
    expandedContent?: React.ReactNode;
}

export interface UserProfileProps {
    name: string;
    email?: string; // Optional email display
    avatar?: string; // URL or initials
    role?: string;
}

interface SidebarProps {
    isCollapsed: boolean;
    onToggle: () => void;
    isMobileOpen: boolean;
    onMobileClose: () => void;
    menuItems: SidebarMenuItem[];
    bottomMenuItems?: SidebarMenuItem[];
    userProfile?: UserProfileProps;
    logo?: React.ReactNode;
    onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
    isCollapsed,
    onToggle,
    isMobileOpen,
    onMobileClose,
    menuItems,
    bottomMenuItems = [],
    logo,
    onLogout
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
    const [hoveredItem, setHoveredItem] = useState<{ label: string, top: number } | null>(null);
    const isDesktop = useMediaQuery('(min-width: 1024px)');

    const isActive = (path?: string) => {
        if (!path) return false;

        // Parse the path to separate pathname and query params
        const [itemPathname, itemSearch] = path.split('?');
        const currentFullPath = location.pathname + location.search;

        // If path has query params, check full match including query
        if (itemSearch) {
            return currentFullPath === path || currentFullPath.startsWith(path + '&');
        }

        // For paths without query params, check pathname only
        // But exclude if current URL has query params that indicate a different view
        if (location.search && location.search.includes('view=')) {
            // Don't match base path if we're in a specific view
            return false;
        }

        // Handle exact match and prefix match for nested routes
        if (location.pathname === itemPathname) return true;
        if (itemPathname !== '/' && location.pathname.startsWith(itemPathname + '/')) return true;

        return false;
    };

    const toggleExpand = (label: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [label]: !prev[label]
        }));
    };

    return (
        <>
            {/* Mobile Overlay */}
            <AnimatePresence>
                {isMobileOpen && !isDesktop && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
                        onClick={onMobileClose}
                    />
                )}
            </AnimatePresence>

            {/* Sidebar Container - Rounded white card design */}
            <div
                className={`
                    fixed bg-white shadow-lg flex flex-col z-50 rounded-2xl
                    ${!isDesktop
                        ? `w-[260px] transition-[left] duration-200 ease-in-out top-4 bottom-4 ${isMobileOpen ? 'left-4' : '-left-[268px]'}`
                        : `left-6 top-8 bottom-8 transition-[width] duration-200 ease-in-out ${isCollapsed ? 'w-[80px]' : 'w-[244px]'}`
                    }
                `}
                style={{ fontFamily: 'Manrope, sans-serif' }}
            >
                {/* Logo Section */}
                <div className={`flex items-center justify-center py-6 border-b border-[#CBCBCB]/30 ${isCollapsed ? 'px-4' : 'px-6'}`}>
                    {logo || (
                        <img
                            src={isCollapsed ? '/branding/logo_blue_short.svg' : '/branding/logo_blue.svg'}
                            alt="Hush Logo"
                            className={`w-auto ${isCollapsed ? 'h-6' : 'h-10'}`}
                        />
                    )}
                </div>

                {/* Collapse Button (Desktop only) */}
                <button
                    onClick={onToggle}
                    className={`
                        hidden lg:flex absolute -right-3 top-10 w-6 h-6 bg-white border border-gray-100 rounded-full
                        items-center justify-center shadow-md text-gray-400 hover:text-[#015FA6] hover:scale-110 transition-all duration-200 z-50
                    `}
                >
                    <ChevronLeft size={14} className={`transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
                </button>

                {/* Menu Items */}
                <div className="flex-1 px-4 py-4 flex flex-col gap-2 overflow-y-auto custom-scrollbar overflow-x-hidden">
                    {menuItems.filter(item => item.show !== false).map((item, index) => {
                        const active = isActive(item.path);
                        const isExpanded = expandedItems[item.label];

                        return (
                            <div key={index} className="relative group">
                                <button
                                    onMouseEnter={(e) => {
                                        if (isCollapsed) {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setHoveredItem({ label: item.label, top: rect.top });
                                        }
                                    }}
                                    onMouseLeave={() => setHoveredItem(null)}
                                    onClick={() => {
                                        if (item.expandable) {
                                            toggleExpand(item.label);
                                        } else if (item.action) {
                                            item.action();
                                        } else if (item.path) {
                                            navigate(item.path);
                                            if (onMobileClose) onMobileClose();
                                        }
                                    }}
                                    className={`
                                            w-full min-h-[48px] rounded-xl flex items-center gap-3 transition-all duration-200 relative overflow-hidden
                                            ${isCollapsed ? 'justify-center px-0' : 'px-4'}
                                            ${active
                                            ? 'bg-gradient-to-r from-[#015FA6]/10 to-[#015FA6]/5 text-[#015FA6]'
                                            : 'text-gray-500 hover:bg-gray-50 hover:text-[#015FA6]'}
                                        `}
                                >
                                    {/* Active Indicator Bar */}
                                    {active && (
                                        <div
                                            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-[#015FA6] rounded-r-full"
                                        />
                                    )}

                                    <div className={`relative z-10 transition-transform duration-200 ${active ? 'scale-110' : 'group-hover:scale-110'}`}>
                                        {typeof item.icon === 'function' ? item.icon(active) : item.icon}
                                    </div>

                                    {!isCollapsed && (
                                        <div className="flex-1 flex items-center justify-between overflow-hidden relative z-10">
                                            <span className={`text-[15px] font-medium truncate ${active ? 'font-semibold' : ''}`}>
                                                {item.label}
                                            </span>
                                            {item.badge && (
                                                <span className="bg-[#FF4D4D] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                                                    {item.badge}
                                                </span>
                                            )}
                                            {item.expandable && (
                                                <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                            )}
                                        </div>
                                    )}
                                </button>

                                {/* Expandable Content */}
                                <AnimatePresence>
                                    {item.expandable && isExpanded && !isCollapsed && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden ml-4 pl-4 border-l border-gray-100"
                                        >
                                            {item.expandedContent ? (
                                                item.expandedContent
                                            ) : (
                                                <div className="py-2 text-sm text-gray-400 italic">
                                                    No items available
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>

                {/* Bottom Section (Logout) */}
                <div className="p-4">
                    {bottomMenuItems.map((item, index) => (
                        <div key={`bottom-${index}`} className="mb-2 relative group">
                            <button
                                onMouseEnter={(e) => {
                                    if (isCollapsed) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setHoveredItem({ label: item.label, top: rect.top });
                                    }
                                }}
                                onMouseLeave={() => setHoveredItem(null)}
                                onClick={item.action || (() => item.path && navigate(item.path))}
                                className={`
                                    w-full min-h-[48px] rounded-lg flex items-center gap-3 transition-all duration-200
                                    ${isCollapsed ? 'justify-center px-0' : 'px-5'}
                                    text-[#757575] hover:bg-gray-50 hover:text-[#0B111E]
                                `}
                            >
                                {typeof item.icon === 'function' ? item.icon(false) : item.icon}
                                {!isCollapsed && <span className="text-base font-medium">{item.label}</span>}
                            </button>
                        </div>
                    ))}

                    {onLogout && (
                        <div className="relative group">
                            <button
                                onMouseEnter={(e) => {
                                    if (isCollapsed) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setHoveredItem({ label: 'Log Out', top: rect.top });
                                    }
                                }}
                                onMouseLeave={() => setHoveredItem(null)}
                                onClick={onLogout}
                                className={`
                                    w-full min-h-[48px] rounded-lg flex items-center gap-3 transition-all duration-200
                                    ${isCollapsed ? 'justify-center px-0' : 'px-5'}
                                    text-[#757575] hover:bg-gray-50 hover:text-[#0B111E]
                                `}
                            >
                                <LogOut size={20} strokeWidth={2} />
                                {!isCollapsed && (
                                    <span className="text-base font-medium">Log Out</span>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Fixed Tooltip Portal/Overlay */}
            <AnimatePresence>
                {isCollapsed && hoveredItem && (
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.15 }}
                        className="fixed z-[60] left-[90px] pointer-events-none"
                        style={{ top: hoveredItem.top + 6 }} // Adjust vertical alignment
                    >
                        <div className="bg-[#0B111E] text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap shadow-xl flex items-center gap-2 relative">
                            {/* Triangle pointer */}
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#0B111E] rotate-45" />
                            <span className="relative z-10">{hoveredItem.label}</span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Sidebar;
